import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import {
  IconLeft,
  IconMore,
  TextSubHeading,
  IconIllustrativeVerified,
  TextCaption2,
  colors,
} from '@blocket/blocket-ui'
import { trackingUserClicksAdInfo } from 'tracking/chat'
import { awaitPulseTracking } from 'utils/pulse'
import AdImage from 'components/pagesContent/Chat/components/shared/AdImage'
import { validateChannel } from '../../../utils/validateChannel'
import ExtensionEntrance from './ExtensionEntrance'
import { lastActive } from '../../../utils/moment'

import {
  InfoWrapper,
  ImageWrapper,
  UserInfoWrapper,
  RecipientWrapper,
  AdInfoWrapper,
  AdInfoLinkWrap,
  AdInfoText,
  ActionButton,
  PriceText,
  Divider,
  BackArrow,
  RecipientLink,
  OnlineIndicator,
  OnlineStatusWrapper,
  ProfileInfoWrapper,
  BankIdVerifiedLogoWrapper,
  BadgesWrapper,
  Badge,
  AdIssueWrapper,
  AdIssueLabelWrapper,
  AdIssueLabel,
} from '../style.js'

const ChannelAdInfo = ({
  isMobile,
  onActionBtnClick,
  recipient,
  recipientName,
  selectedChannel,
  extensionData,
  isAdReplyView,
  overlayActive,
  profileData,
  recipientBlocketAccountId,
  recipientVerified,
}) => {
  const { ad, adIssueText, adIsActive } = validateChannel(selectedChannel)
  const adUrl = ad?.url
  const router = useRouter()

  const hasExtensionDataContent = Boolean(
    extensionData?.extensions?.length ||
      extensionData?.activeExtensions?.length ||
      extensionData?.transaction,
  )

  const onAdLinkClick = useCallback(
    (e) => {
      e.preventDefault()

      awaitPulseTracking(
        () => trackingUserClicksAdInfo(selectedChannel, ad),
        () => (window.location = adUrl),
      )
    },
    [ad, adUrl, selectedChannel],
  )

  const userOnline = recipient?.connectionStatus === 'online'
  const hideAdInfo = isAdReplyView && !isMobile
  const badges = profileData?.badges
  const backNavigation = () => (isAdReplyView ? window.history.back() : router.push('/meddelanden'))

  const renderBadges = badges?.map((badge, i) => (
    <Badge key={badge + i} title={badge?.description}>
      {badge?.component}
    </Badge>
  ))

  return (
    <React.Fragment>
      <InfoWrapper data-testid="channel-ad-info">
        <UserInfoWrapper>
          {isMobile && (
            <BackArrow data-testid="back-button" role="button" onClick={backNavigation}>
              <IconLeft height={24} width={24} />
            </BackArrow>
          )}
          {recipient && (
            <RecipientWrapper data-testid="recipient-info">
              <ProfileInfoWrapper>
                {recipientBlocketAccountId ? (
                  <TextSubHeading as={RecipientLink} href={`/profil/${recipientBlocketAccountId}`}>
                    {recipientName}
                  </TextSubHeading>
                ) : (
                  <TextSubHeading>{recipientName}</TextSubHeading>
                )}
                {recipientVerified && (
                  <BankIdVerifiedLogoWrapper>
                    <IconIllustrativeVerified width={16} height={16} />
                  </BankIdVerifiedLogoWrapper>
                )}
              </ProfileInfoWrapper>
              {userOnline ? (
                <OnlineStatusWrapper>
                  <OnlineIndicator />
                  <TextCaption2>Aktiv</TextCaption2>
                </OnlineStatusWrapper>
              ) : (
                Boolean(recipient?.lastSeenAt) && (
                  <TextCaption2>{lastActive(recipient.lastSeenAt)}</TextCaption2>
                )
              )}
            </RecipientWrapper>
          )}
          {badges && !isMobile && <BadgesWrapper>{renderBadges}</BadgesWrapper>}
          {!isAdReplyView && (
            <div style={{ position: 'relative', marginLeft: '8px' }}>
              <ActionButton
                onClick={onActionBtnClick}
                aria-label="Ta bort konversation, rapportera användare eller blockera användare"
              >
                <IconMore height={20} width={20} />
              </ActionButton>
            </div>
          )}
        </UserInfoWrapper>
        <Divider style={{ minHeight: 1 }} />
        {!hideAdInfo && (
          <AdInfoWrapper data-testid="ad-info" isMobile={isMobile}>
            <AdInfoLinkWrap onClick={onAdLinkClick} href={adUrl}>
              <ImageWrapper fade={adIssueText}>
                <AdImage imgSrc={ad?.image} />
              </ImageWrapper>
              <AdIssueWrapper>
                <AdIssueLabelWrapper>
                  {adIssueText && (
                    <AdIssueLabel
                      data-testid="ad-status-label"
                      text={adIssueText}
                      textColor={colors.grey800}
                      backgroundColor={colors.grey200}
                    />
                  )}
                  <AdInfoText adIssueText>{ad.subject}</AdInfoText>
                </AdIssueLabelWrapper>
                <PriceText>{ad.price}</PriceText>
              </AdIssueWrapper>
            </AdInfoLinkWrap>
          </AdInfoWrapper>
        )}
        {hasExtensionDataContent && (
          <ExtensionEntrance
            extensionData={extensionData}
            selectedChannel={selectedChannel}
            isMobile={isMobile}
            adIsActive={adIsActive}
            overlayActive={overlayActive}
          />
        )}
      </InfoWrapper>
    </React.Fragment>
  )
}

ChannelAdInfo.propTypes = {
  // State if current device is a mobile.
  isMobile: PropTypes.bool,
  // Toggles action options - three dots top right corner.
  onActionBtnClick: PropTypes.func,
  // Recipient user (https://docs.sendbird.com/platform/user#_4_resource_representation).
  recipient: PropTypes.object,
  recipientName: PropTypes.string,
  // Current user selected channel (https://docs.sendbird.com/platform/group_channel#_4_resource_representation).
  selectedChannel: PropTypes.object,
  // Response data from extension endpoint (https://github.schibsted.io/blocket/messaging-be/blob/master/schemas/openapi.yml#L86).
  extensionData: PropTypes.object,
  // If rendered in AdReplyView.
  isAdReplyView: PropTypes.bool,
  // Ads overlay on ad section.
  overlayActive: PropTypes.bool,
  recipientBlocketAccountId: PropTypes.string,
  // Respone from profile-be/v1/public-profiles/{blocket_account_id} with formatted badges components
  profileData: PropTypes.object,
  recipientVerified: PropTypes.bool,
}

export default ChannelAdInfo
